// @ts-nocheck
import React, { useEffect } from 'react';
import KwPanel from '../../common/KwPanel';

import GamesCompressedView from './GamesCompressedView';
import GamesUncompressedView from './GamesUncompressedView';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { debounce } from '../../../util/debounceUtils';
import icons from '../../../constants/icons';
import { hasPermission, isPowerUser } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import Button from '../../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks';

export interface GamesProps {
  requests: any[];
  processing?: boolean;
  onApproveAll: () => void;
  onDeclineAll: () => void;
  onApproveItem: (request: any) => void;
  onDeclineItem: (request: any) => void;
  onWithdrawRequest: (request: any) => void;
  onDownloadKeys: (request: any) => void;
  onUploadKeys: (request: any) => void;
  onKeysOrdered: (request: any) => void;
  onSteamRequest: (request: any) => void;
  onSetStatus: (request: any) => void;
  onShowDetails: (request: any) => void;
  onEditRequestValue: (request: any) => void;
  onEditRequestKeyCount: (request: any) => void;
  onDisableKeys: (request: any) => void;
  onReassign: (request: any) => void;
  searchFilter: string;
  onSearchFilterChange: (value: string) => void;
  endUserDetailsRequired: boolean;
  handleUploadAccountDetails: (request: any) => void;
  handleReturnKeys: (request: any) => void;
}

function Games({
  requests,
  processing,
  onApproveAll,
  onDeclineAll,
  onApproveItem,
  onDeclineItem,
  onWithdrawRequest,
  onDownloadKeys,
  onUploadKeys,
  onKeysOrdered,
  onSteamRequest,
  onSetStatus,
  onShowDetails,
  onEditRequestValue,
  onEditRequestKeyCount,
  onDisableKeys,
  onReassign,
  searchFilter,
  onSearchFilterChange,
  endUserDetailsRequired,
  handleUploadAccountDetails,
  handleReturnKeys,
}: GamesProps) {
  const { t } = useTranslation('requests');
  const [compressed, setCompressed] = useLocalStorage<boolean>(
    'keyRequestCompressedView',
    false
  );

  const currentUser = useAppSelector((state) => state.auth);

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      if (window.innerWidth < 991 && compressed) {
        setCompressed(false);
      }
    }, 1000);

    window.addEventListener('resize', debouncedHandleResize);

    return (_) => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  const handleSearchFilterChange = (el: any) => {
    onSearchFilterChange(el.target.value);
  };

  const allRequestsVisible = () => {
    return (
      requests.filter(
        (request) =>
          request.requestStatus?.isPending ||
          request.requestStatus?.isKeysOrdered
      ).length > 0
    );
  };
  const howManyCanBeSigned = () => {
    return requests.filter((request) => request.currentUserCanSign).length;
  };
  const areAllRequestsApproved = () => {
    let approvedRequests = requests.filter(
      (request) => request.action === 'approve' && request.currentUserCanSign
    ).length;
    let b = howManyCanBeSigned();
    return approvedRequests === b && b > 0;
  };

  const areAllRequestsDeclined = () => {
    let declinedRequests = requests.filter(
      (request) => request.action === 'decline' && request.currentUserCanSign
    ).length;
    let b = howManyCanBeSigned();
    return declinedRequests === b && b > 0;
  };

  // ================================================================================
  // Button display logic
  let showAllButtons = false; // set to true to see all buttons in dev

  const canDisableKeys = (request: any) => {
    return (
      !request.isWaitingForKeys &&
      request.requestStatus.isAuthorised &&
      hasPermission(currentUser, permissionTypes.MANAGE_KEYS)
    );
  };

  const canAssignRequest = () => {
    return isPowerUser(currentUser);
  };

  const canDownloadKeys = (request: any) => {
    return showAllButtons || request.allowDownloadKeys;
  };

  const canUploadKeys = (request: any) => {
    return showAllButtons || request.allowUploadKeys;
  };

  const canSetKeysOrdered = (request: any) => {
    return (
      showAllButtons ||
      (request.allowUploadKeys && !request.requestStatus.isKeysOrdered)
    );
  };

  const canRequestOnSteam = (request: any) => {
    return showAllButtons || request.allowUploadKeys;
  };

  const canSetStatus = (request: any) => {
    return showAllButtons || request.allowChangeStatus;
  };

  const canWithdrawRequest = (request: any) => {
    return showAllButtons || request.allowWithdrawRequest;
  };

  const canReturnKeys = (request: any) => {
    return showAllButtons || request.allowReturnKeys;
  };

  return (
    <KwPanel className="mb-4">
      <div className="space-bottom space-row-sm space-row-even">
        <div className="space-bottom-xs d-flex" style={{ overflow: 'hidden' }}>
          <div className="me-2 d-none d-md-flex ">
            <button
              className={`btn ${compressed
                ? 'btn-default btn-outline btn--square'
                : 'btn-primary btn-outline btn--square'
                } r-corners-left`}
              onClick={() => setCompressed(false)}
            >
              <FontAwesomeIcon
                icon={icons.TABLE_GROUPED}
                fixedWidth
                aria-hidden="true"
              />
            </button>
            <button
              className={`btn ${compressed
                ? 'btn-primary btn-outline btn--square'
                : 'btn-default btn-outline btn--square'
                } r-corners-right`}
              onClick={() => setCompressed(true)}
            >
              <FontAwesomeIcon
                icon={icons.TABLE_UNGROUPED}
                fixedWidth
                aria-hidden="true"
              />
            </button>
          </div>
          <div
            className="search-wrapper in-panel"
            style={{ width: 'calc(100% - 44px)' }}
          >
            <input
              type="text"
              className="form-control r-corners-left"
              placeholder={t('Search titles')}
              value={searchFilter}
              onChange={handleSearchFilterChange}
            />

            <div className="search-icon">
              <FontAwesomeIcon icon={icons.SEARCH} />
            </div>
          </div>
        </div>
        <div className="text-right space-row-xs space-row-even">
          {allRequestsVisible() && howManyCanBeSigned() > 1 && (
            <div className="btn-group me-md-4">
              <Button
                isDisabled={processing}
                title={processing ? t('Processing') : t('Approve')}
                className={`btn btn-default btn--approval space-right-s-xs ${areAllRequestsApproved() ? 'active' : ''
                  }`}
                onClick={onApproveAll}
                icon={icons.APPROVE}
                hideTitle={true}
                text={t('Approve')}
              />
              <Button
                isDisabled={processing}
                title={processing ? t('Processing') : t('Decline')}
                className={`btn btn-default btn--decline space-left-s-xs${areAllRequestsDeclined() ? 'active' : ''
                  }`}
                onClick={onDeclineAll}
                icon={icons.DECLINE}
                hideTitle={true}
                text={t('Decline')}
              />
            </div>
          )}
        </div>
      </div>

      {compressed ? (
        <GamesCompressedView
          requests={requests}
          canWithdrawRequest={canWithdrawRequest}
          processing={processing || false}
          onWithdrawRequest={onWithdrawRequest}
          canSetStatus={canSetStatus}
          onSetStatus={onSetStatus}
          canDownloadKeys={canDownloadKeys}
          onDownloadKeys={onDownloadKeys}
          canRequestOnSteam={canRequestOnSteam}
          onSteamRequest={onSteamRequest}
          canUploadKeys={canUploadKeys}
          onUploadKeys={onUploadKeys}
          canSetKeysOrdered={canSetKeysOrdered}
          onKeysOrdered={onKeysOrdered}
          onShowDetails={onShowDetails}
          onApproveItem={onApproveItem}
          onDeclineItem={onDeclineItem}
          onEditRequestKeyCount={onEditRequestKeyCount}
          onEditRequestValue={onEditRequestValue}
          canDisableKeys={canDisableKeys}
          onDisableKeys={onDisableKeys}
          canAssignRequest={canAssignRequest}
          onReassign={onReassign}
          showAllButtons={showAllButtons}
          endUserDetailsRequired={endUserDetailsRequired}
          handleUploadAccountDetails={handleUploadAccountDetails}
          canReturnKeys={canReturnKeys}
          handleReturnKeys={handleReturnKeys}
          currentUser={currentUser}
        />
      ) : (
        <GamesUncompressedView
          requests={requests}
          canWithdrawRequest={canWithdrawRequest}
          processing={processing || false}
          onWithdrawRequest={onWithdrawRequest}
          canSetStatus={canSetStatus}
          onSetStatus={onSetStatus}
          canDownloadKeys={canDownloadKeys}
          onDownloadKeys={onDownloadKeys}
          canRequestOnSteam={canRequestOnSteam}
          onSteamRequest={onSteamRequest}
          canUploadKeys={canUploadKeys}
          onUploadKeys={onUploadKeys}
          canSetKeysOrdered={canSetKeysOrdered}
          onKeysOrdered={onKeysOrdered}
          onShowDetails={onShowDetails}
          onApproveItem={onApproveItem}
          onDeclineItem={onDeclineItem}
          onEditRequestKeyCount={onEditRequestKeyCount}
          onEditRequestValue={onEditRequestValue}
          onDisableKeys={onDisableKeys}
          canDisableKeys={canDisableKeys}
          canAssignRequest={canAssignRequest}
          onReassign={onReassign}
          showAllButtons={showAllButtons}
          endUserDetailsRequired={endUserDetailsRequired}
          handleUploadAccountDetails={handleUploadAccountDetails}
          canReturnKeys={canReturnKeys}
          handleReturnKeys={handleReturnKeys}
          currentUser={currentUser}
        />
      )}
    </KwPanel>
  );
}

export default Games;
